import { useEffect, useMemo, useRef, useState } from "react";
import { api, API, getToken } from "@/lib/api";
import Composer from "@/components/Composer";
import AdSlot from "@/components/AdSlot";
import { isNativeAndroid } from "@/lib/payments";
import Message from "@/components/Message";
import MediaPanel from "@/components/MediaPanel";
import { Search, Loader2, Images, Menu, Store, Upload, Palette } from "lucide-react";
import DrawDividerOverlay from "@/components/DrawDividerOverlay";
import { toast } from "sonner";
import PublishDialog from "@/components/PublishDialog";
import ColorPickerDialog from "@/components/ColorPickerDialog";
import { useLongPressReorder } from "@/lib/useLongPressReorder";
import { useEdgeAutoScroll } from "@/lib/useEdgeAutoScroll";
import { nearestRow } from "@/lib/nearestRow";
import { normalizeUrl, sha256Hex } from "@/lib/dedupe";
import { getLocalFileUrl } from "@/lib/offline";

export default function ChatArea({ title, titles, onActivityChange, onOpenSearch, onOpenSidebar, jumpToItemId, onJumpConsumed, onTitleUpdate, onOpenItem }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  // When set, the color dialog edits that specific item instead of the thread.
  const [colorPickerItemId, setColorPickerItemId] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  // In-page item drag-reorder state.
  const [dragItemId, setDragItemId] = useState(null);
  const [overItemId, setOverItemId] = useState(null);
  const [overItemPos, setOverItemPos] = useState(null); // "above" | "below"
  const scrollerRef = useRef(null);
  const composerRef = useRef(null);
  const prevCountRef = useRef(0);
  const keepScrollRef = useRef(false);
  // dragenter/leave fire for every child; count depth so leaving a child
  // doesn't dismiss the overlay prematurely.
  const dragDepthRef = useRef(0);

  useEffect(() => {
    setLoading(true);
    setItems([]);
    (async () => {
      try {
        const r = await api.get(`/titles/${title.title_id}/items`);
        setItems(r.data);
      } catch {
        toast.error("Failed to load messages");
      } finally {
        setLoading(false);
      }
    })();
  }, [title.title_id]);

  // Cross-device trash sync: if a note/file in this thread was deleted from
  // another device (or this one, backgrounded) re-pull on refocus so it
  // doesn't keep showing here.
  useEffect(() => {
    const sync = async () => {
      try {
        const r = await api.get(`/titles/${title.title_id}/items`);
        setItems(r.data);
      } catch { /* background sync — stay quiet (Workspace handles a trashed thread) */ }
    };
    const onVisible = () => { if (document.visibilityState === "visible") sync(); };
    window.addEventListener("focus", sync);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", sync);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [title.title_id]);

  useEffect(() => {
    // Only auto-scroll to the bottom when the list GROWS (new message / upload)
    // or right after the initial load. Editing an existing note in-place must
    // keep the viewport exactly where the user was reading, so we skip the
    // scroll when the item count is unchanged.
    const el = scrollerRef.current;
    if (!el) return;
    const prev = prevCountRef.current;
    const next = items.length;
    const grew = next > prev;
    const justLoaded = !loading && prev === 0 && next > 0;
    if ((grew || justLoaded) && !keepScrollRef.current) {
      el.scrollTop = el.scrollHeight;
    }
    keepScrollRef.current = false;
    prevCountRef.current = next;
  }, [items, loading]);

  // Reset the counter when switching threads so the first render of a new
  // thread still snaps to the bottom.
  useEffect(() => { prevCountRef.current = 0; }, [title.title_id]);

  // When told to jump to a specific item, scroll to it + briefly highlight it.
  const jumpTo = (itemId) => {
    const el = document.querySelector(`[data-testid="message-${itemId}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightId(itemId);
    setTimeout(() => setHighlightId(null), 2400);
  };

  useEffect(() => {
    if (!jumpToItemId || loading) return;
    if (!items.some((it) => it.item_id === jumpToItemId)) return;
    // Give the messages one frame to render
    const t = setTimeout(() => {
      jumpTo(jumpToItemId);
      onJumpConsumed?.();
    }, 80);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jumpToItemId, loading, items, onJumpConsumed]);

  // Same link or file already saved? Checks this chat first, then the user's other notes.
  const findDuplicate = async ({ url, files }) => {
    if (url) {
      const key = normalizeUrl(url);
      const local = items.find((it) => it.type === "link" && it.url && normalizeUrl(it.url) === key);
      if (local) return { item: local };
      const r = await api.post("/items/find-duplicate", { url, exclude_title_id: title.title_id });
      return r.data.match ? { item: r.data.match, title: r.data.match } : null;
    }
    const hashed = await Promise.all(files.map(async (f) => ({ name: f.name, size: f.size, sha256: await sha256Hex(f) })));
    const existing = items.filter((it) => it.type === "file");
    for (const f of hashed) {
      const hit = existing.find((it) => (it.sha256 ? it.sha256 === f.sha256 : it.size === f.size && it.file_name === f.name));
      if (hit) return { item: group0Of(hit) };
    }
    const r = await api.post("/items/find-duplicate", { files: hashed, exclude_title_id: title.title_id });
    return r.data.match ? { item: r.data.match, title: r.data.match } : null;
  };
  const group0Of = (it) => groupedItems.find((g) => g.some((x) => x.item_id === it.item_id))?.[0] || it;

  const handleSend = async (payload) => {
    try {
      const r = await api.post(`/titles/${title.title_id}/items`, payload);
      setItems((prev) => [...prev, r.data]);
      onActivityChange?.();
    } catch (e) {
      toast.error("Could not save");
    }
  };

  const handleUpload = async (file, caption, onProgress, groupId) => {
    const fd = new FormData();
    fd.append("file", file);
    if (caption) fd.append("caption", caption);
    if (groupId) fd.append("group_id", groupId);
    const r = await api.post(`/titles/${title.title_id}/uploads`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (evt) => {
        if (!onProgress) return;
        const total = evt.total || file.size;
        onProgress(total ? Math.min(100, Math.round((evt.loaded / total) * 100)) : 0);
      },
    });
    setItems((prev) => [...prev, r.data]);
    onActivityChange?.();
  };

  // --- Drag & drop ----------------------------------------------------------
  // Dropped files are *attached* to the composer (not uploaded yet) so the user
  // can type a caption and send file + text together.
  const attachDropped = (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    if (title.published_product_id) {
      toast.error("This note is published — unpublish it to add files");
      return;
    }
    composerRef.current?.attachFiles(list);
    toast.success(`${list.length} file${list.length === 1 ? "" : "s"} attached — add a note and press send`);
  };

  // Listen on the window so a file can be dropped anywhere on screen — the
  // sidebar, the header, the calendar — not just over the message list.
  useEffect(() => {
    const hasFiles = (e) =>
      !!e.dataTransfer && Array.from(e.dataTransfer.types || []).includes("Files");
    const onEnter = (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepthRef.current += 1;
      setIsDragging(true);
    };
    const onOver = (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
      if (!dragDepthRef.current) {
        dragDepthRef.current = 1;
        setIsDragging(true);
      }
    };
    const onLeave = (e) => {
      if (!hasFiles(e)) return;
      // relatedTarget is null when the pointer actually leaves the window.
      dragDepthRef.current = e.relatedTarget ? Math.max(0, dragDepthRef.current - 1) : 0;
      if (dragDepthRef.current === 0) setIsDragging(false);
    };
    const onDropAnywhere = (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepthRef.current = 0;
      setIsDragging(false);
      const files = e.dataTransfer.files;
      if (files && files.length) attachDropped(files);
    };
    window.addEventListener("dragenter", onEnter);
    window.addEventListener("dragover", onOver);
    window.addEventListener("dragleave", onLeave);
    window.addEventListener("drop", onDropAnywhere);
    return () => {
      window.removeEventListener("dragenter", onEnter);
      window.removeEventListener("dragover", onOver);
      window.removeEventListener("dragleave", onLeave);
      window.removeEventListener("drop", onDropAnywhere);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title.title_id, title.published_product_id]);

  // Paste a screenshot (or any copied file) straight into the composer.
  useEffect(() => {
    const onPaste = (e) => {
      const files = Array.from(e.clipboardData?.items || [])
        .filter((it) => it.kind === "file")
        .map((it) => it.getAsFile())
        .filter(Boolean)
        .map((f, i) => {
          if (f.name && f.name !== "image.png") return f;
          const ext = (f.type.split("/")[1] || "png").replace("jpeg", "jpg");
          const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
          return new File([f], `screenshot-${stamp}${i ? `-${i + 1}` : ""}.${ext}`, { type: f.type });
        });
      if (!files.length) return;
      e.preventDefault();
      if (title.published_product_id) {
        toast.error("This note is published — unpublish it to add files");
        return;
      }
      composerRef.current?.attachFiles(files);
      toast.success(
        files.length === 1 ? "Pasted — add a note and press send" : `${files.length} files pasted`,
      );
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title.title_id, title.published_product_id]);

  const handleDelete = async (item_id) => {
    try {
      await api.delete(`/items/${item_id}`);
      setItems((prev) => prev.filter((x) => x.item_id !== item_id));
      onActivityChange?.();
    } catch {
      toast.error("Could not delete");
    }
  };

  // Photos uploaded together (same group_id) render as one note with a grid.
  const groupedItems = useMemo(() => {
    const out = [];
    for (const it of items) {
      const last = out[out.length - 1];
      if (it.group_id && last && last[0].group_id === it.group_id) last.push(it);
      else out.push([it]);
    }
    return out;
  }, [items]);

  const handleEdit = async (item_id, content, url) => {
    try {
      const r = await api.patch(`/items/${item_id}`, url === undefined ? { content } : { content, url });
      setItems((prev) => prev.map((x) => (x.item_id === item_id ? r.data : x)));
      onActivityChange?.();
      toast.success("Updated");
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
      return false;
    }
  };

  const handleRemoveAttachments = async (item_ids, content) => {
    try {
      const r = await api.post("/items/remove-attachments", { item_ids, content });
      const gone = new Set(r.data.deleted);
      const upd = Object.fromEntries(r.data.updated.map((u) => [u.item_id, u]));
      setItems((prev) => prev.filter((x) => !gone.has(x.item_id)).map((x) => upd[x.item_id] || x));
      onActivityChange?.();
      toast.success(gone.size === 1 ? "Deleted" : `Deleted ${gone.size} items`);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete — check your connection");
      return false;
    }
  };

  const handleMove = async (item_id, target_title_id) => {
    const group = groupedItems.find((g) => g[0].item_id === item_id) || [{ item_id }];
    const ids = group.map((x) => x.item_id);
    try {
      let r;
      for (const id of ids) r = await api.patch(`/items/${id}`, { title_id: target_title_id });
      setItems((prev) => prev.filter((x) => !ids.includes(x.item_id)));
      onActivityChange?.();
      const target = (titles || []).find((t) => t.title_id === target_title_id);
      toast.success(`Moved to "${target?.name || "note"}"`);
      return r.data;
    } catch {
      toast.error("Could not move");
    }
  };

  // --- In-thread drag reorder -----------------------------------------------
  const reorderItems = async (newOrder) => {
    const prev = items;
    setItems(newOrder);
    try {
      await api.post(`/titles/${title.title_id}/items/reorder`, {
        item_ids: newOrder.map((x) => x.item_id),
      });
    } catch {
      toast.error("Could not save new order");
      setItems(prev);
    }
  };

  const [drawMode, setDrawMode] = useState(false);
  const onDividerDrawn = async (y) => {
    setDrawMode(false);
    const hit = nearestRow(listRef.current, y);
    try {
      const r = await api.post(`/titles/${title.title_id}/items`, { type: "divider", content: "" });
      const groups = groupedItems.slice();
      let idx = hit ? groups.findIndex((g) => g[0].item_id === hit.id) : -1;
      idx = idx < 0 ? groups.length : idx + (hit.pos === "below" ? 1 : 0);
      groups.splice(idx, 0, [r.data]);
      keepScrollRef.current = true;
      await reorderItems(groups.flat());
      onActivityChange?.();
    } catch {
      toast.error("Could not add the line");
    }
  };

  const reorderGroups = (fromId, toId, pos) => {
    const from = groupedItems.findIndex((g) => g[0].item_id === fromId);
    let to = groupedItems.findIndex((g) => g[0].item_id === toId);
    if (from < 0 || to < 0) return;
    if (pos === "below") to += 1;
    if (from < to) to -= 1;
    const next = groupedItems.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    reorderItems(next.flat());
  };

  // Touch-friendly long-press reorder for notes/messages on mobile. Declared
  // BEFORE buildItemDragHandlers so the handler can read `touch*` state.
  const touchItem = useLongPressReorder({
    items: groupedItems,
    idOf: (g) => g[0].item_id,
    onReorder: (next) => reorderItems(next.flat()),
  });

  // Edge auto-scroll while an HTML5 drag is in progress inside the chat.
  useEdgeAutoScroll(!!dragItemId);

  const buildItemDragHandlers = (it) => ({
    isDragging: dragItemId === it.item_id || touchItem.dragId === it.item_id,
    dropAbove: (overItemId === it.item_id && overItemPos === "above") || (touchItem.overId === it.item_id && touchItem.overPos === "above"),
    dropBelow: (overItemId === it.item_id && overItemPos === "below") || (touchItem.overId === it.item_id && touchItem.overPos === "below"),
    onTouchStart: touchItem.onTouchStart(it.item_id),
    onDragStart: (e) => {
      setDragItemId(it.item_id);
      try {
        e.dataTransfer.effectAllowed = "move";
        // Non-"Files" marker so the file-drop overlay doesn't fire.
        e.dataTransfer.setData("application/x-notelosh-item", it.item_id);
      } catch { /* ignore */ }
    },
    onDragEnd: () => { setDragItemId(null); setOverItemId(null); setOverItemPos(null); },
  });

  const listRef = useRef(null);
  const onListDragOver = (e) => {
    if (!dragItemId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const hit = nearestRow(listRef.current, e.clientY);
    const id = hit && hit.id !== dragItemId ? hit.id : null;
    const pos = id ? hit.pos : null;
    if (id !== overItemId) setOverItemId(id);
    if (pos !== overItemPos) setOverItemPos(pos);
  };
  const onListDrop = (e) => {
    if (!dragItemId) return;
    e.preventDefault();
    e.stopPropagation();
    const hit = nearestRow(listRef.current, e.clientY);
    const fromId = dragItemId;
    setDragItemId(null); setOverItemId(null); setOverItemPos(null);
    if (hit && hit.id !== fromId) reorderGroups(fromId, hit.id, hit.pos);
  };

  const token = getToken();
  const fileUrlFor = (item_id) => getLocalFileUrl(item_id) || `${API}/files/${item_id}?auth=${encodeURIComponent(token || "")}`;

  // After an offline sync, refresh this chat from the server.
  useEffect(() => {
    const onSynced = async () => {
      try { const r = await api.get(`/titles/${title.title_id}/items`); setItems(r.data); } catch { /* keep cache */ }
    };
    window.addEventListener("notelosh:synced", onSynced);
    return () => window.removeEventListener("notelosh:synced", onSynced);
  }, [title.title_id]);

  return (
    <div
      className="flex flex-col h-full min-w-0 relative"
      data-testid="chat-area-dropzone"
    >
      {isDragging && (
        <div
          data-testid="chat-dropzone-overlay"
          className="fixed inset-0 z-50 pointer-events-none"
        >
          <div className="absolute inset-2 rounded-[28px] border-2 border-dashed border-[#3B82F6]/70 bg-[#3B82F6]/[0.06]" />
          <div className="absolute bottom-28 left-1/2 -translate-x-1/2 flex items-center gap-2.5 rounded-full bg-[#121212]/95 border border-[#3B82F6]/50 pl-4 pr-5 py-2.5 shadow-[0_12px_40px_-12px_rgba(37,99,235,0.6)]">
            <Upload className="h-4 w-4 text-[#3B82F6]" />
            <span className="text-sm font-semibold text-[#F3F4F6]">Drop anywhere to attach</span>
            <span className="text-[11px] text-[#9CA3AF] hidden sm:inline">· up to 50 MB each</span>
          </div>
        </div>
      )}
      {/* Header */}
      <header className="notelosh-chatarea-header h-16 flex items-center justify-between gap-4 px-4 sm:px-6 border-b border-[#1E1E1E] bg-[#0A0A0A]/60 backdrop-blur-md">
        <div className="flex items-center gap-3 min-w-0">
          <button
            data-testid="open-sidebar-btn"
            onClick={onOpenSidebar}
            aria-label="Open menu"
            className="md:hidden h-10 w-10 shrink-0 rounded-full bg-[#1E1E1E] hover:bg-[#27272A] border border-[#27272A] text-[#9CA3AF] hover:text-[#F3F4F6] flex items-center justify-center transition-all"
          >
            <Menu className="h-4 w-4" />
          </button>
          <button
            type="button"
            data-testid="chat-header-color-btn"
            onClick={() => { if (title.published_product_id) return; setColorPickerItemId(null); setColorPickerOpen(true); }}
            disabled={!!title.published_product_id}
            aria-label="Change bubble color"
            title={title.published_product_id ? title.name : "Color"}
            className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-semibold text-white shrink-0 relative group/avatar transition-all ${title.published_product_id ? "cursor-default" : "hover:ring-2 hover:ring-white/20 cursor-pointer"}`}
            style={{ background: `linear-gradient(135deg, ${title.color}, ${title.color}99)` }}
          >
            {title.name?.[0]?.toUpperCase() || "T"}
            {!title.published_product_id && (
              <span className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-[#0A0A0A] border border-[#27272A] flex items-center justify-center opacity-0 group-hover/avatar:opacity-100 transition-opacity">
                <Palette className="h-2.5 w-2.5 text-[#9CA3AF]" />
              </span>
            )}
          </button>
          <div className="min-w-0">
            <div data-testid="chat-title-name" className="font-heading text-base font-semibold truncate">{title.name}</div>
            <div className="text-xs text-[#6B7280]">{title.item_count || 0} items</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="header-publish-btn"
            onClick={() => {
              if (!navigator.onLine) { toast("You're offline — publishing needs an internet connection"); return; }
              setPublishOpen(true);
            }}
            title={title.published_product_id ? "Manage marketplace listing" : "Publish this note to the Marketplace"}
            className="flex items-center gap-2 px-3.5 py-2 rounded-full border text-sm transition-all bg-[#1E1E1E] border-[#27272A] text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-[#27272A]"
          >
            <Store className="h-4 w-4" />
            <span className="hidden md:inline">{title.published_product_id ? "Published" : "Publish"}</span>
          </button>
          <button
            data-testid="header-media-btn"
            onClick={() => setMediaOpen(true)}
            title="Photos, files & links in this chat"
            className="flex items-center gap-2 px-3.5 py-2 rounded-full bg-[#1E1E1E] hover:bg-[#27272A] border border-[#27272A] text-sm text-[#9CA3AF] hover:text-[#F3F4F6] transition-all"
          >
            <Images className="h-4 w-4" />
            <span className="hidden sm:inline">Media & files</span>
          </button>
          <button
            data-testid="header-ai-search-btn"
            onClick={onOpenSearch}
            title={`Ask AI within "${title.name}"`}
            className="flex items-center gap-2 px-3.5 py-2 rounded-full bg-[#1E1E1E] hover:bg-[#27272A] border border-[#27272A] text-sm text-[#9CA3AF] hover:text-[#F3F4F6] transition-all"
          >
            <Search className="h-4 w-4" />
            <span className="hidden sm:inline">Ask about this note</span>
          </button>
        </div>
      </header>

      {/* Main row: messages + right ad rail */}
      <div className="flex-1 min-h-0 flex overflow-hidden">
        {/* Messages column */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Messages */}
          <div
            ref={scrollerRef}
            onDragOver={onListDragOver}
            onDrop={onListDrop}
            className="flex-1 overflow-y-auto px-4 sm:px-8 py-4"
          >
            {loading ? (
              <div className="h-full flex items-center justify-center text-[#6B7280] text-sm gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : items.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center gap-2 px-6">
                <div className="text-[#9CA3AF] text-sm">No items yet in <span className="text-[#F3F4F6] font-medium">{title.name}</span></div>
                <div className="text-xs text-[#6B7280]">Paste a URL, write a note, or attach a file to start.</div>
              </div>
            ) : (
              <div ref={listRef} data-testid="chat-message-list" className="max-w-3xl mx-auto space-y-5">
                {groupedItems.map((group) => (
                  <Message
                    key={group[0].item_id}
                    item={group[0]}
                    group={group.length > 1 ? group : undefined}
                    onDelete={handleDelete}
                    onEdit={handleEdit}
                    onRemoveAttachments={handleRemoveAttachments}
                    onMove={handleMove}
                    titles={titles}
                    highlight={group.some((g) => g.item_id === highlightId)}
                    fileUrl={group[0].type === "file" ? fileUrlFor(group[0].item_id) : null}
                    fileUrlFor={fileUrlFor}
                    bubbleColor={title.color}
                    onChangeBubbleColor={(itemId) => {
                      setColorPickerItemId(itemId || null);
                      setColorPickerOpen(true);
                    }}
                    locked={!!title.published_product_id}
                    dragHandlers={
                      title.published_product_id
                        ? undefined
                        : buildItemDragHandlers(group[0])
                    }
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right vertical ad rail (desktop web only) */}
        {!isNativeAndroid() && (
          <aside
            data-testid="chat-right-rail"
            className="hidden xl:flex w-[240px] shrink-0 border-l border-[#1E1E1E] bg-[#0A0A0A]/40 backdrop-blur-md flex-col gap-3 px-3 py-4 overflow-y-auto"
          >
            <AdSlot slot="chat-rail-1" variant="vertical" />
            <AdSlot slot="chat-rail-2" variant="vertical" />
          </aside>
        )}
      </div>

      {/* Composer */}
      <div className="border-t border-[#1E1E1E] bg-[#0A0A0A]/70 backdrop-blur-md px-4 sm:px-8 py-4">
        <div className="max-w-3xl mx-auto">
          {title.published_product_id ? (
            <div
              data-testid="published-lock-banner"
              className="rounded-2xl border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B] px-4 py-3 text-sm flex items-start gap-2"
            >
              <span className="font-semibold shrink-0">🔒 Locked:</span>
              <span>
                This note is <b>published</b> {title.published_group_id ? "in a private group" : "to the Marketplace"}.
                You need to <b>unpublish and publish again</b> in order to edit this note.
              </span>
            </div>
          ) : (
            <Composer ref={composerRef} onSend={handleSend} onUpload={handleUpload} findDuplicate={findDuplicate} onDrawLine={title.published_product_id ? undefined : () => setDrawMode(true)} onFindExisting={(itemId, otherTitleId) => (otherTitleId ? onOpenItem?.({ title_id: otherTitleId, item_id: itemId }) : jumpTo(itemId))} />
          )}
        </div>
      </div>

      <MediaPanel
        open={mediaOpen}
        onOpenChange={setMediaOpen}
        items={items}
        fileUrlFor={fileUrlFor}
        titleName={title.name}
      />
      {drawMode && <DrawDividerOverlay onDrawn={onDividerDrawn} onCancel={() => setDrawMode(false)} />}
      <PublishDialog
        open={publishOpen}
        onOpenChange={setPublishOpen}
        title={title}
        onPublished={() => onActivityChange?.()}
        onUnpublished={() => onActivityChange?.()}
      />
      <ColorPickerDialog
        open={colorPickerOpen}
        onOpenChange={(o) => { setColorPickerOpen(o); if (!o) setColorPickerItemId(null); }}
        scope={colorPickerItemId ? "message" : "thread"}
        currentColor={
          colorPickerItemId
            ? (items.find((x) => x.item_id === colorPickerItemId)?.color || title.color)
            : title.color
        }
        threadName={title.name}
        onSave={async (color) => {
          try {
            if (colorPickerItemId) {
              const r = await api.patch(`/items/${colorPickerItemId}`, { color });
              setItems((prev) => prev.map((x) => (x.item_id === colorPickerItemId ? r.data : x)));
              toast.success("Note color updated");
            } else {
              if (!onTitleUpdate) return;
              await onTitleUpdate(title.title_id, { color });
              toast.success("Thread color updated");
            }
          } catch {
            toast.error("Could not save color");
          }
        }}
        onReset={async () => {
          if (!colorPickerItemId) return;
          try {
            const r = await api.patch(`/items/${colorPickerItemId}`, { color: "" });
            setItems((prev) => prev.map((x) => (x.item_id === colorPickerItemId ? r.data : x)));
            toast.success("Color reset to thread");
          } catch {
            toast.error("Could not reset color");
          }
        }}
      />
    </div>
  );
}
